import { useState, useCallback, useMemo } from 'react'
import {
    SpaCategory,
    SpaSelectionState,
    SpaTreatment,
} from 'routes/Upsell/SpaCreator/types/spa'
import { useAccommodation } from 'store/accommodation-slice/hooks'
import {
    ListItemSpaTreatmentPresenter,
    SpaTreatmentVariantDto,
    useListCreatorSpaTreatmentsQuery,
} from 'store/api-slice/hooks'
import { useApp } from 'store/steps'
import { useTickets } from 'store/ticket-slice/hooks'

export const useSpaSelection = () => {
    const { language } = useApp()
    const { ticketsDates } = useTickets()
    const { accommodationDates } = useAccommodation()

    const { isError: isFetchingTreatmentsError, data: treatmentsData } =
        useListCreatorSpaTreatmentsQuery({
            _locale: language,
        })

    const [selection, setSelection] = useState<SpaSelectionState>({
        selectedCategoryId: null,
        selectedTreatmentId: null,
        selectedTreatmentData: null,
        selectedVariantId: null,
        selectedVariantData: null,
        selectedDate: null,
        selectedStartTime: null,
        selectedEndTime: null,
    })

    // TODO: possible refactor/simpification - discuss with be data structure given from api
    // ev. move some logic into helpers
    const treatmentsByCategories: SpaCategory[] | undefined = useMemo(() => {
        if (!treatmentsData || !treatmentsData.items) return undefined

        return treatmentsData.items.reduce((categories, currentTreatment) => {
            const {
                categoryId,
                categoryName,
                spaTreatmentGroupId,
                name,
                variants,
            } = currentTreatment

            if (!categoryId || !categoryName || !spaTreatmentGroupId || !name)
                return categories

            const existingCategoryIndex = categories.findIndex(
                (category) => category.id === categoryId
            )
            const minTreatmentPrice = variants
                ? variants.reduce((minPrice, currentVariant) => {
                      if (
                          minPrice === undefined ||
                          (currentVariant.price !== undefined &&
                              currentVariant.price < minPrice)
                      )
                          return currentVariant.price

                      return minPrice
                  }, undefined as SpaCategory['minPrice'])
                : undefined

            const treatmentPrices: number[] = variants
                ? variants
                      .map((variant) => variant.price || -1)
                      // TODO: decide if consider only one occurence of each price
                      //   .filter(
                      //       (element, index, array) =>
                      //           array.indexOf(element) === index && element !== -1
                      //   )
                      .sort()
                : []

            const treatmentRegularPrices: number[] = variants
                ? variants.map((variant) => variant.regularPrice || -1).sort()
                : []

            const treatmentHistoryPrices: number[] = variants
                ? variants
                      .map((variant) => variant.historyLowestPrice || -1)
                      .sort()
                : []

            const treatmentTimes: number[] = variants
                ? variants
                      .map((variant) => variant.duration || -1)
                      .filter(
                          (element, index, array) =>
                              array.indexOf(element) === index && element !== -1
                      )
                      .sort()
                : []

            const extendedCurrentTreatment: SpaTreatment = {
                ...currentTreatment,
                prices: [...treatmentPrices],
                regularPrices: [...treatmentRegularPrices],
                historyPrices: [...treatmentHistoryPrices],
                times: [...treatmentTimes],
            }

            // add current treatment to existing category...
            if (existingCategoryIndex !== -1) {
                const categoryMinPrice =
                    categories[existingCategoryIndex].minPrice

                categories[existingCategoryIndex].treatments.push(
                    extendedCurrentTreatment
                )

                if (
                    categoryMinPrice === undefined ||
                    (minTreatmentPrice !== undefined &&
                        minTreatmentPrice < categoryMinPrice)
                )
                    categories[existingCategoryIndex].minPrice =
                        minTreatmentPrice

                return categories
            }

            // ...or create new category with current treatment
            return [
                ...categories,
                {
                    id: categoryId,
                    name: categoryName,
                    minPrice: minTreatmentPrice,
                    treatments: [extendedCurrentTreatment],
                },
            ]
        }, [] as SpaCategory[])
    }, [treatmentsData])

    const availableDates = ticketsDates.length
        ? ticketsDates
        : accommodationDates

    const selectTreatment = useCallback(
        (treatmentId: string, treatmentData: ListItemSpaTreatmentPresenter) => {
            setSelection((prev) => ({
                ...prev,
                selectedTreatmentId: treatmentId,
                selectedTreatmentData: treatmentData,
                selectedVariantId: null,
                selectedVariantData: null,
                selectedDate: null,
                selectedStartTime: null,
                selectedEndTime: null,
            }))
        },
        []
    )

    // TODO: refactor to use one function for all steps
    const selectVariant = useCallback(
        (variantId: string, variantData: SpaTreatmentVariantDto) => {
            setSelection((prev) => ({
                ...prev,
                selectedVariantId: variantId,
                selectedVariantData: variantData,
                selectedDate: null,
                selectedStartTime: null,
                selectedEndTime: null,
            }))
        },
        []
    )

    const selectDate = useCallback((date: string) => {
        setSelection((prev) => ({
            ...prev,
            selectedDate: date,
            selectedStartTime: null,
            selectedEndTime: null,
        }))
    }, [])

    const selectTime = useCallback(
        (startTime: string | null, endTime: string | null) => {
            setSelection((prev) => ({
                ...prev,
                selectedStartTime: startTime,
                selectedEndTime: endTime,
            }))
        },
        []
    )

    const setInitialSelection = useCallback((selection: SpaSelectionState) => {
        setSelection(selection)
    }, [])

    return {
        categories: treatmentsByCategories,
        isCategoriesError: isFetchingTreatmentsError,
        availableDates,
        selection,
        selectTreatment,
        selectVariant,
        selectDate,
        selectTime,
        setInitialSelection,
    }
}
